/**
 * THE write path for a user's own personal profile.
 *
 * WHY THIS EXISTS
 *   `src/app/edit-profile.tsx` and `src/app/edit-company.tsx` looked like save
 *   screens and were not. Neither imported `supabase` at all. Their handlers
 *   awaited a `setTimeout` — 500ms and 800ms respectively — then wrote to the
 *   persisted Zustand store and returned success. So an edit survived a restart
 *   on THAT device (AsyncStorage, key `inverio-profile`), was invisible to every
 *   other device, to the web app, to admin views and to anything server-side,
 *   and vanished entirely at sign-out when the reset cascade clears the store.
 *   The fake delay is what made it convincing: the spinner, the pause, the
 *   success state were all real. Only the save was not.
 *
 *   The app already had a correct write in `(startup)/userprofile.tsx`. The bug
 *   was not missing knowledge, it was a second screen re-implementing a save
 *   from scratch and stopping one step short. So the cure is not "fix the two
 *   screens" — it is to leave exactly one place where a personal profile can be
 *   written, and have every screen call it.
 *
 * WHAT IT GUARANTEES
 *   1. The server write happens FIRST and its error is surfaced. supabase-js
 *      resolves with `{ error }` rather than throwing, so an unchecked write is
 *      silent failure (see grep-guard ARCH-06) — this throws on it.
 *   2. The store is updated only AFTER the server confirms, so local state can
 *      never claim a save the database rejected.
 *   3. `role` IS NOT WRITABLE HERE. It is persona, it is never derived from
 *      which UI branch rendered, and administrative authority lives in
 *      `admin_roles` (see the ADMIN AUTHORITY INVARIANT in CLAUDE.md).
 *      edit-profile previously wrote `role: isStartup ? formData.role :
 *      'investor'`, which silently demoted any admin who saved their profile.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { rpc } from '@/lib/supabase/rpc';
import { useProfileStore } from '@/lib/state/profile-store';
import {
  buildPersonalProfileDbUpdate,
  splitName,
  type PersonalProfilePatch,
} from './profile-edit-fields';

// `splitName` moved to profile-edit-fields.ts (the update builder needs it, and
// this module imports that one). Re-exported so existing callers are unaffected.
export { splitName };

/**
 * The fields a person may change about themselves.
 *
 * Deliberately NOT here: `role` (persona — changed only by select-role and
 * onboarding), `email` (owned by GoTrue; changing it is an auth flow with a
 * confirmation step, not a profile edit), and anything about a company or an
 * investor firm (those are separate entities with their own screens).
 *
 * EVERY FIELD IS OPTIONAL, and that is load-bearing. The screen now saves one
 * section at a time, so a patch carries only the fields that section renders.
 * `name` used to be required here, which was correct when a single Save
 * submitted the whole form and wrong the moment "About" could be saved alone.
 * Absent means "leave it"; present-but-empty means "clear it".
 *
 * `functionTitle` requires `startupId`. Omit both to leave it untouched.
 */
export type PersonalProfileEdit = PersonalProfilePatch;

/**
 * Persist the caller's own profile, then reconcile local state.
 *
 * RLS scopes `profiles` to `auth.uid() = id`, so this can only ever write the
 * caller's own row — the user id comes from the session, never from an argument.
 */
export function useSavePersonalProfile(startupId?: string | null) {
  const queryClient = useQueryClient();
  const user = useProfileStore((s) => s.user);
  const setUser = useProfileStore((s) => s.setUser);

  return useMutation({
    mutationFn: async (edit: PersonalProfileEdit) => {
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth.user?.id;
      if (!userId) {
        throw new Error('You are signed out. Sign in again to save your profile.');
      }

      // PATCH, not overwrite. A section save carries only its own fields, so
      // building all five columns unconditionally would NULL out whatever the
      // user did not open — see the header of profile-edit-fields.ts.
      const update = buildPersonalProfileDbUpdate(edit);

      // Server first. The error is checked, not discarded (ARCH-06).
      // An empty patch means this section changed nothing on `profiles` (it may
      // still carry a functionTitle below); issuing `.update({})` would be an
      // unbound write for no reason.
      if (Object.keys(update).length > 0) {
        const { error } = await supabase.from('profiles').update(update).eq('id', userId);
        if (error) throw error;
      }

      // Company function title, when the caller has a startup and supplied one.
      // Separate call because it is a different table with a different
      // authorization rule — exactly as (startup)/userprofile.tsx does it.
      if (startupId && edit.functionTitle !== undefined) {
        const { error: fnError } = await rpc('update_my_function_title', {
          p_startup_id: startupId,
          p_function_title: edit.functionTitle,
        });
        if (fnError) throw fnError;
      }

      return update;
    },

    onSuccess: (update) => {
      // Only now does local state change. Note `role` is untouched: this
      // mutation cannot alter persona, and cannot alter authority at all.
      //
      // The store update is a patch for the same reason the column update is:
      // rebuilding the whole user object from a one-section save would blank
      // the fields that section never carried, and the store is persisted — so
      // the DB would be right and the screen would be wrong until a refetch.
      // `name` and `email` are required on UserInfo, so they are always present
      // even when this patch does not touch them.
      const next = { ...user, name: user?.name ?? '', email: user?.email ?? '' };

      if ('first_name' in update) {
        next.name = [update.first_name, update.last_name].filter(Boolean).join(' ');
      }
      if ('bio' in update) next.bio = (update.bio as string | null) ?? undefined;
      if ('location' in update) next.location = (update.location as string | null) ?? undefined;
      if ('linkedin_url' in update) {
        next.linkedIn = (update.linkedin_url as string | null) ?? undefined;
      }

      setUser(next);
      // The route guards and any screen reading the server profile must see the
      // new values without a relaunch.
      queryClient.invalidateQueries({ queryKey: ['user-role'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['my-startup'] });
    },
  });
}
